<template>
  <div
    class="equipment-accordion"
    :class="{
      'is-expanded': expanded,
      'is-collapsible': canToggle
    }"
    @click="onRowClick"
  >
    <div
      ref="content"
      class="equipment-accordion__text"
      :class="{ 'is-expanded': expanded }"
    >{{ visibleText }}</div>
    <button
      v-if="canToggle"
      type="button"
      class="equipment-accordion__toggle"
      :aria-expanded="String(expanded)"
      :aria-label="expanded ? 'Plegar equipos afectados' : 'Desplegar equipos afectados'"
      :title="expanded ? 'Plegar' : 'Desplegar'"
      @click.stop="toggle"
    >
      <i
        class="fas fa-chevron-down equipment-accordion__icon"
        :class="{ 'is-expanded': expanded }"
        aria-hidden="true"
      ></i>
    </button>
  </div>
</template>

<script>
function equipmentItems(value) {
  const raw = value == null ? '' : String(value).trim();
  if (!raw) return [];
  return raw.split(/[,;]+/).map((item) => item.trim()).filter(Boolean);
}

export default {
  name: 'ExpandableEquipmentList',
  props: {
    text: {
      type: [String, Number],
      default: ''
    }
  },
  data() {
    return {
      expanded: false,
      overflows: false
    };
  },
  computed: {
    items() {
      return equipmentItems(this.text);
    },
    visibleText() {
      if (this.items.length === 0) {
        return this.text == null ? '' : String(this.text);
      }
      if (this.expanded && this.items.length > 1) {
        return this.items.join('\n');
      }
      return this.items.join(', ');
    },
    canToggle() {
      return this.overflows || this.expanded;
    }
  },
  watch: {
    text() {
      this.expanded = false;
      this.overflows = false;
      this.scheduleMeasure();
    }
  },
  mounted() {
    this.scheduleMeasure();
    this.onResize = () => this.measureOverflow();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
  },
  methods: {
    scheduleMeasure() {
      this.$nextTick(() => {
        requestAnimationFrame(() => this.measureOverflow());
      });
    },
    measureOverflow() {
      if (this.expanded) return;
      const el = this.$refs.content;
      if (!el || !el.clientWidth) return;
      this.overflows = el.scrollWidth > el.clientWidth + 1;
    },
    onRowClick() {
      if (this.canToggle) this.toggle();
    },
    toggle() {
      if (!this.canToggle) return;
      this.expanded = !this.expanded;
      if (!this.expanded) {
        this.scheduleMeasure();
      }
    }
  }
};
</script>

<style scoped>
.equipment-accordion {
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  gap: 0.45rem;
}

.equipment-accordion.is-collapsible {
  cursor: pointer;
}

.equipment-accordion__text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 0.4em;
}

.equipment-accordion__text.is-expanded {
  white-space: pre-wrap;
  overflow: visible;
  text-overflow: clip;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.equipment-accordion__toggle {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.2em;
  padding: 0.1rem 0.2rem;
  border: 0;
  background: transparent;
  color: #495057;
  line-height: 1;
  cursor: pointer;
}

.equipment-accordion__toggle:hover,
.equipment-accordion__toggle:focus-visible {
  color: #0d6efd;
}

.equipment-accordion__toggle:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
  border-radius: 3px;
}

.equipment-accordion__icon {
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.equipment-accordion__icon.is-expanded {
  transform: rotate(180deg);
}
</style>

<style>
/* Reserve vertical scrollbar space so expanding the equipment list does not shift the modal horizontally. */
.incident-detail-modal-body {
  overflow-y: scroll;
  scrollbar-gutter: stable;
}

.modal:has(.incident-detail-modal-body) {
  scrollbar-gutter: stable;
}
</style>
